import { useMemo } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ATTRAPPE, Fehler, hole, loesche } from "./klient";
import * as AUF from "./attrappe/aufnahmen";
import { mitMappe } from "./mappe";
import { uuid } from "./speicher";
import { einreihen, useUebertraegtGerade, useWarteschlange, zuruecknehmen } from "./warteschlange";
import type { Wartender } from "./warteschlange";
import { BLICKWINKEL } from "@mrmachine/fach";
import type { Blickwinkel } from "@mrmachine/fach";

/* ══════════════════════════════════════════════════════════════════
   Das Foto als Zeile — am Telefon (Migration 0120, gebaut 17.09.2026).

   Luca, 17.09.2026: *„Fotos immer hochladbar, mit Verschmutzungsgrad,
   Papiere usw."*

   **Ein Weg für jedes Bild: auf das Gerät, dann hinaus.** Das Bild geht in
   die Warteschlange (Art `foto`), auch mit Netz. Der Zug legt es ab
   (`POST /ablage`) und schreibt dann die Zeile (`POST /fotos`). So gibt es
   keinen zweiten Pfad „mit Netz sofort", der sich anders verhält als der
   im Funkloch — und kein Abschluss wartet je auf einen Upload.

   **Die ID kommt vom Gerät**, und sie ist dieselbe für die Eintragung in
   der Warteschlange und für die Zeile. Ein zweiter Versuch nach einem
   Funkloch mitten in der Antwort legt nichts doppelt an (`schon_da`).

   **Was der Schirm zeigt, ist beides zusammen:** die Zeilen vom Server
   und die Bilder, die noch auf dem Gerät liegen — jedes mit seinem Stand
   (wartet, lädt, abgelegt, abgewiesen). Ohne das sähe der Fahrer im
   Funkloch ein Foto nicht, das er gerade gemacht hat, und machte es noch
   einmal.

   **Die Kategorie sagt, was darauf ist; der Kontext, woran es hängt.**
   Drei Kategorien verlangen eine Angabe, und zwar schon hier und nicht erst
   in der Datenbank: ein Schaden die Beschreibung, eine Verschmutzung die
   Stufe, die Papiere ja oder nein.
   ══════════════════════════════════════════════════════════════════ */

export type Fotokontext =
  | "vorbereitung" | "ausgabe" | "einsatztag" | "ruecknahme" | "schaden" | "ortsaufnahme" | "menge";

export const FOTOKATEGORIEN = [
  "uebersicht", "zaehler", "schaden", "verschmutzung", "papiere", "sonstiges",
  /* **Die vier der Ortsaufnahme** (Migration 0156). Luca, 21.09.2026: *„Wenn
     ich Fotos bei einer Ortsaufnahme erfasse, wäre es sinnvoll, wenn man auch
     sagen kann: Was ist das für ein Foto? Ist das vielleicht ein Foto vom
     Kranplatz?"* */
  "stellplatz", "zufahrt", "flaeche", "hindernis",
] as const;
export type Fotokategorie = (typeof FOTOKATEGORIEN)[number];

/**
 * **Wonach vor Ort fotografiert wird** — in der Reihenfolge des Schirms.
 *
 * Die drei Arten mit Pflichtangabe (`schaden`, `verschmutzung`, `papiere`)
 * stehen nicht darin: Sie verlangen ein zweites Feld, und ein Bild aus der
 * Ortsaufnahme trägt keins. `zaehler` gehört an die Maschine, nicht an die
 * Wiese.
 */
export const AUFNAHMEARTEN = [
  "uebersicht", "stellplatz", "zufahrt", "flaeche", "hindernis", "sonstiges",
] as const;
export type Aufnahmeart = (typeof AUFNAHMEARTEN)[number];

/** Ein Bild auf dem Weg zum Abschluss — sein Pfad und was darauf ist (0156). */
export type Fotoeintrag = { pfad: string; art: Aufnahmeart };

export const VERSCHMUTZUNG = ["sauber", "leicht", "stark"] as const;
export type Verschmutzung = (typeof VERSCHMUTZUNG)[number];

/** Was eine Kategorie mitbringt — Angaben, die nicht zu ihr gehören, gehen nicht mit. */
export type Fotoangaben = {
  kategorie: Fotokategorie;
  verschmutzung: Verschmutzung | null;
  papiereVollstaendig: boolean | null;
  schadenNotiz: string;
  schadenNeu: boolean | null;
  notiz: string;
  /**
   * **Welchen Platz der Pflichtliste das Bild füllt** (0173) — „Foto vorne".
   * Nur an Vorbereitung, Übergabe und Rücknahme; sonst `null`.
   */
  blickwinkel: Blickwinkel | null;
  /**
   * **Zu welcher Prüffrage das Bild gehört** (L1, Migration 0185) — die
   * Kamera an einer Frage der Vorbereitung. Fehlt oder `null`: zu keiner.
   */
  frageId?: string | null;
};

export const LEERE_ANGABEN: Fotoangaben = {
  kategorie: "uebersicht",
  verschmutzung: null,
  papiereVollstaendig: null,
  schadenNotiz: "",
  schadenNeu: null,
  notiz: "",
  blickwinkel: null,
  frageId: null,
};

/**
 * Was an einer Angabe fehlt — ein i18n-Pfad, oder `null`, wenn sie vollständig ist.
 * Dieselbe Regel wie `angabenPruefen` in `dienste/foto.ts`.
 */
export function angabeFehlt(a: Fotoangaben): string | null {
  if (a.kategorie === "schaden" && !a.schadenNotiz.trim()) return "einsatz.foto.fehltSchaden";
  if (a.kategorie === "verschmutzung" && a.verschmutzung === null) return "einsatz.foto.fehltVerschmutzung";
  if (a.kategorie === "papiere" && a.papiereVollstaendig === null) return "einsatz.foto.fehltPapiere";
  return null;
}

/** Woran ein Foto hängt. Der Server leitet Position, Vorgang und Maschine daraus ab. */
export type Fotobezug = {
  belegungId?: string;
  defektId?: string;
  maschineId?: string;
  /**
   * **Die Ortsaufnahme** (21.09.2026) — der Bezug, den der Kontext
   * `ortsaufnahme` verlangt (`photo_bezug_je_kontext`, 0120).
   *
   * Bis hierher sammelte der Schirm vor Ort nur Ablagepfade und schickte sie
   * beim Abschließen als Liste. Damit gab es das Fotomodul zweimal: einmal
   * mit „Was zeigt es?", Kacheln und Stand (überall sonst), einmal als zwei
   * Knöpfe ohne Angabe (in der Ortsaufnahme).
   */
  aufnahmeId?: string;
  /** Der Kalendertag `JJJJ-MM-TT` beim Einsatztag — leer heißt: der Tag der Aufnahme. */
  tag?: string;
};

/** Ein Bild, wie der Schirm es zeigt — vom Server oder noch auf dem Gerät. */
export type Bild = {
  id: string;
  kontext: Fotokontext;
  kategorie: Fotokategorie;
  verschmutzung: Verschmutzung | null;
  papiereVollstaendig: boolean | null;
  schadenNotiz: string;
  schadenNeu: boolean | null;
  notiz: string;
  aufgenommenAm: string;
  tag: string;
  belegungId: string;
  defektId: string;
  /** Der Ablagepfad — leer, solange das Bild nur auf dem Gerät liegt. */
  pfad: string;
  /** Das Bild selbst, solange es noch nicht abgelegt ist. */
  blob: Blob | null;
  erfasstVon: string;
  /** `wartet` auf dem Gerät · `laedt` geht gerade hinaus · `abgelegt` beim Server · `stockt` abgewiesen */
  stand: "wartet" | "laedt" | "abgelegt" | "stockt";
  /** Der Grund, wenn es abgewiesen wurde — ein i18n-Pfad. */
  grund: string | null;
  /** Verwerfen geht, solange nichts abgeschlossen ist (0120). */
  darfVerwerfen: boolean;
  /** Der Platz der Pflichtliste, den es füllt (0173) — `null` bei allen anderen. */
  blickwinkel: Blickwinkel | null;
  /** Die Prüffrage der Vorbereitung, an der es aufgenommen wurde (0185) — sonst leer. */
  frageId: string;
};

type Roh = Record<string, unknown>;
const txt = (w: unknown): string => (w === null || w === undefined ? "" : String(w));
const jaNein = (w: unknown): boolean | null => (typeof w === "boolean" ? w : null);
const alsKategorie = (w: unknown): Fotokategorie =>
  FOTOKATEGORIEN.find((k) => k === w) ?? "sonstiges";
const alsVerschmutzung = (w: unknown): Verschmutzung | null =>
  VERSCHMUTZUNG.find((k) => k === w) ?? null;
const alsBlickwinkel = (w: unknown): Blickwinkel | null =>
  BLICKWINKEL.find((k) => k === w) ?? null;

function ausServer(z: Roh): Bild {
  return {
    id: txt(z["id"]),
    kontext: txt(z["kontext"]) as Fotokontext,
    kategorie: alsKategorie(z["kategorie"]),
    verschmutzung: alsVerschmutzung(z["verschmutzung"]),
    papiereVollstaendig: jaNein(z["papiere_vollstaendig"]),
    schadenNotiz: txt(z["schaden_notiz"]),
    schadenNeu: jaNein(z["schaden_neu"]),
    notiz: txt(z["notiz"]),
    aufgenommenAm: txt(z["aufgenommen_am"]),
    tag: txt(z["tag"]),
    belegungId: txt(z["belegung_id"]),
    defektId: txt(z["defekt_id"]),
    pfad: txt(z["pfad"]),
    blob: null,
    erfasstVon: txt(z["erfasst_von"]),
    stand: "abgelegt",
    grund: null,
    darfVerwerfen: z["darf_verwerfen"] === true,
    blickwinkel: alsBlickwinkel(z["blickwinkel"]),
    frageId: txt(z["frage_id"]),
  };
}

function ausGeraet(w: Wartender, gerade: string | null): Bild {
  const n = w.nutzlast;
  const anhang = w.dateien.find((d) => d.feld === "pfad");
  return {
    id: txt(n["id"]) || w.id,
    kontext: txt(n["kontext"]) as Fotokontext,
    kategorie: alsKategorie(n["kategorie"]),
    verschmutzung: alsVerschmutzung(n["verschmutzung"]),
    papiereVollstaendig: jaNein(n["papiere_vollstaendig"]),
    schadenNotiz: txt(n["schaden_notiz"]),
    schadenNeu: jaNein(n["schaden_neu"]),
    notiz: txt(n["notiz"]),
    aufgenommenAm: txt(n["aufgenommen_am"]) || w.erfasstAm,
    tag: txt(n["tag"]),
    belegungId: txt(n["belegung_id"]),
    defektId: txt(n["defekt_id"]),
    pfad: txt(n["pfad"]),
    blob: anhang?.blob ?? null,
    erfasstVon: "",
    stand: w.abgewiesen ? "stockt" : gerade === w.id ? "laedt" : "wartet",
    grund: w.grund,
    // Was noch auf dem Gerät liegt, gehört niemandem sonst: Es geht immer.
    darfVerwerfen: gerade !== w.id,
    blickwinkel: alsBlickwinkel(n["blickwinkel"]),
    frageId: txt(n["frage_id"]),
  };
}

/* ══════════════ Lesen ══════════════ */

/**
 * Woran die Bilder hängen — **eines von beidem muss stehen**, sonst fragt
 * niemand (die API antwortet auf einen Aufruf ohne Bezug mit `422`).
 *
 * `aufnahme` kam am 18.09.2026 dazu: Seit Welle 2 (0131) trägt keine Antwort
 * mehr eine Liste von Pfaden, und die Ortsbegehung liest ihre Bilder deshalb
 * über denselben Weg wie das Protokoll.
 */
export type Fotofilter = {
  belegung?: string;
  aufnahme?: string;
  kontext?: Fotokontext;
  tag?: string;
};

/** Der Bezug als Zeichenkette — leer heißt: Es gibt nichts zu fragen. */
const bezugVon = (f: Fotofilter | null): string => f?.belegung || f?.aufnahme || "";

export async function fotosLesen(f: Fotofilter): Promise<Bild[]> {
  if (ATTRAPPE) {
    /* **Die erledigte Begehung hat drei Bilder** (18.09.2026). Sie liegen
       als Pfade in `attrappe/aufnahmen.ts`; das Bild dahinter ist ein
       schraffierter Platzhalter, den `ablageUrl` baut — im Prototyp gibt es
       keinen Behälter, und ein leeres Feld sähe aus wie ein Ladefehler. */
    const pfade = f.aufnahme ? AUF.FOTOPFADE[f.aufnahme] ?? [] : [];
    /* **Drei Arten, nicht dreimal dieselbe** (0156): Übersicht, Stellplatz,
       Zufahrt — so ist im Prototyp zu sehen, wofür die Arten da sind. Die
       Reihenfolge folgt den Pfaden in `attrappe/aufnahmen.ts`. */
    const arten = ["uebersicht", "stellplatz", "zufahrt"] as const;
    return pfade.map((pfad, i): Bild => ({
      id: `${f.aufnahme ?? ""}-foto-${i + 1}`,
      kontext: "ortsaufnahme",
      kategorie: arten[i] ?? "sonstiges",
      verschmutzung: null,
      papiereVollstaendig: null,
      schadenNotiz: "",
      schadenNeu: null,
      notiz: "",
      aufgenommenAm: "",
      tag: "",
      belegungId: "",
      defektId: "",
      pfad,
      blob: null,
      erfasstVon: "",
      stand: "abgelegt",
      grund: null,
      darfVerwerfen: false,
      blickwinkel: null,
      frageId: "",
    }));
  }
  const such = new URLSearchParams();
  if (f.belegung) such.set("belegung", f.belegung);
  if (f.aufnahme) such.set("aufnahme", f.aufnahme);
  if (f.kontext) such.set("kontext", f.kontext);
  if (f.tag) such.set("tag", f.tag);
  const roh = await hole<Roh>(`/fotos?${such.toString()}`);
  return (Array.isArray(roh["zeilen"]) ? (roh["zeilen"] as Roh[]) : []).map(ausServer);
}

export const fotoSchluessel = {
  alle: ["fotos"] as const,
  bezug: (f: Fotofilter) =>
    ["fotos", f.belegung ?? "", f.aufnahme ?? "", f.kontext ?? "", f.tag ?? ""] as const,
};

/**
 * Die Bilder eines Einsatzes — vom Server und vom Gerät, neueste zuerst.
 *
 * **Über die Mappe:** Wer die Fotos einmal mit Netz gesehen hat, sieht sie
 * auch im Funkloch. Was seitdem dazukam, liegt ohnehin auf dem Gerät.
 */
export function useBilder(f: Fotofilter | null): { bilder: Bild[]; laedt: boolean; fehler: boolean } {
  const bezug = bezugVon(f);
  const abfrage = useQuery({
    queryKey: f ? fotoSchluessel.bezug(f) : ["fotos", "nichts"],
    queryFn: () => mitMappe(
      `fotos/${f?.belegung ?? ""}/${f?.aufnahme ?? ""}/${f?.kontext ?? ""}/${f?.tag ?? ""}`,
      () => fotosLesen(f!),
    ),
    enabled: !!bezug,
  });
  const wartende = useWarteschlange();
  const gerade = useUebertraegtGerade();

  const bilder = useMemo(() => {
    /* **Was auf dem Gerät wartet, gehört dazu** — an der Belegung wie an der
       Ortsaufnahme (21.09.2026). Bis dahin sammelte die Begehung Pfade und
       schickte sie mit dem Ergebnis; seit sie dasselbe Fotomodul benutzt wie
       alle anderen Masken, hängt jedes Bild sofort an ihr — und ohne Netz
       steht es in der Warteschlange. Es dort nicht zu zeigen hieße: Der
       Vertriebler sieht sein eigenes Foto nicht und macht es noch einmal. */
    if (!f?.belegung && !f?.aufnahme) return abfrage.data ?? [];
    const passt = (w: { nutzlast: Record<string, unknown> }): boolean => (f.belegung
      ? txt(w.nutzlast["belegung_id"]) === f.belegung
      : txt(w.nutzlast["aufnahme_id"]) === f.aufnahme);
    const vomGeraet = wartende
      .filter((w) => w.art === "foto" && passt(w))
      .filter((w) => !f.kontext || w.nutzlast["kontext"] === f.kontext)
      .filter((w) => !f.tag || !w.nutzlast["tag"] || w.nutzlast["tag"] === f.tag)
      .map((w) => ausGeraet(w, gerade));
    const schon = new Set(vomGeraet.map((b) => b.id));
    const alle = [...vomGeraet, ...(abfrage.data ?? []).filter((b) => !schon.has(b.id))];
    return alle.sort((a, b) => b.aufgenommenAm.localeCompare(a.aufgenommenAm));
  }, [f, wartende, gerade, abfrage.data]);

  return { bilder, laedt: abfrage.isPending && !!bezug, fehler: abfrage.isError };
}

/* ══════════════ Aufnehmen ══════════════ */

export type Fotoaufnahme = {
  blob: Blob;
  kontext: Fotokontext;
  bezug: Fotobezug;
  angaben: Fotoangaben;
  /** Was im Warteband dasteht: „KR-01 · Bauer Hochbau". */
  betreff: string;
  /** Wann das Bild entstand — die Uhr des Geräts. */
  aufgenommenAm?: Date;
};

/** Legt das Bild auf das Gerät. Gibt die ID zurück — die der Zeile beim Server. */
export async function fotoAufnehmen(e: Fotoaufnahme): Promise<string> {
  const id = uuid();
  const a = e.angaben;
  const am = e.aufgenommenAm ?? new Date();
  await einreihen({
    id,
    art: "foto",
    weg: "/fotos",
    betreff: e.betreff,
    erfasstAm: am,
    nutzlast: {
      id,
      kontext: e.kontext,
      kategorie: a.kategorie,
      aufgenommen_am: am.toISOString(),
      ...(e.bezug.belegungId ? { belegung_id: e.bezug.belegungId } : {}),
      ...(e.bezug.defektId ? { defekt_id: e.bezug.defektId } : {}),
      ...(e.bezug.maschineId ? { maschine_id: e.bezug.maschineId } : {}),
      ...(e.bezug.aufnahmeId ? { aufnahme_id: e.bezug.aufnahmeId } : {}),
      ...(e.bezug.tag ? { tag: e.bezug.tag } : {}),
      // Nur, was zur Kategorie gehört — ein Schadenstext an einem Zählerfoto
      // wäre eine Angabe, die niemand gemacht hat.
      ...(a.kategorie === "schaden"
        ? { schaden_notiz: a.schadenNotiz.trim(), ...(a.schadenNeu === null ? {} : { schaden_neu: a.schadenNeu }) }
        : {}),
      ...(a.kategorie === "verschmutzung" ? { verschmutzung: a.verschmutzung } : {}),
      ...(a.kategorie === "papiere" ? { papiere_vollstaendig: a.papiereVollstaendig } : {}),
      ...(a.notiz.trim() ? { notiz: a.notiz.trim() } : {}),
      // Der Platz der Pflichtliste (0173) — nur, wo einer gemeint ist.
      ...(a.blickwinkel ? { blickwinkel: a.blickwinkel } : {}),
      // Die Prüffrage (0185) — das Bild hängt fest an ihr, nicht nur über den Text.
      ...(a.frageId ? { frage_id: a.frageId } : {}),
    },
    dateien: [{ feld: "pfad", zweck: "foto", blob: e.blob }],
  });
  return id;
}

/**
 * Ein Bild verwerfen — solange nichts abgeschlossen ist.
 *
 * **Liegt es noch auf dem Gerät, wird es dort zurückgenommen** und erreicht
 * den Server nie. Sonst `DELETE /fotos/:id`; das räumt die Ablagedatei mit.
 * Ohne Netz geht das zweite nicht, und das sagt der Fehler.
 */
export function useBildVerwerfen() {
  const kunde = useQueryClient();
  return useMutation({
    mutationFn: async (b: Pick<Bild, "id" | "stand">) => {
      if (b.stand !== "abgelegt") {
        if (await zuruecknehmen(b.id)) return;
        // Es ging gerade hinaus — dann liegt es jetzt beim Server.
      }
      if (ATTRAPPE) return;
      await loesche(`/fotos/${encodeURIComponent(b.id)}`);
    },
    onSettled: () => void kunde.invalidateQueries({ queryKey: fotoSchluessel.alle }),
  });
}

/** Der i18n-Pfad eines Fehlers beim Verwerfen. */
export function verwerfenFehler(e: unknown): string {
  if (e instanceof Fehler && e.schluessel === "foto.abgeschlossen") return "einsatz.foto.schonAbgeschlossen";
  if (e instanceof Fehler && (e.schluessel === "netz" || e.status === 0)) return "einsatz.foto.verwerfenOhneNetz";
  return e instanceof Fehler ? e.textPfad : "fehler.unbekannt";
}
